import { useEffect, useState } from "react";
import { Supplier } from "./types";
import { FormField, FormSingleSelectField } from "../../../../actions/Tenants/config/constantsTyped";
import { Form, FormOptions } from "../PageBase";

interface GrowerRelationFormObject {
    fruit_type: string;
    grower: string[];
}

export const defineGrowerRelationFormCreate = (onSaveSupplier: (supplier: Supplier) => void) => (formOptions: FormOptions | undefined, supplier: Supplier | undefined) => {
    const fruitOptions = (supplier?.fruit_types ?? []).map(
        (fruit_type) => ({
            value: fruit_type,
            label: (formOptions?.fruit_type ?? []).find((option) => option.value === fruit_type)?.label ?? fruit_type
        })
    );

    const growerOptions = formOptions?.grower ?? [];
    const [growersField, setGrowersField] = useState<FormSingleSelectField>({
        label: "Grower(s)",
        name: "grower",
        type: "single-select",
        mandatory: true,
        multi: true,
        return_array: true,
        options: growerOptions
    });

    useEffect(() => {
        const fruit_type = fruitOptions?.[0]?.value;

        setGrowersField({
            ...growersField,
            options: growerOptions.filter((grower) => (grower as any).fruit_type === fruit_type && supplier?.country === (grower as any).country)
        });
    }, [supplier]);

    const growerRelationForm: FormField[] = [
        {
            label: "Fruit type",
            name: "fruit_type",
            type: "radio",
            mandatory: true,
            options: fruitOptions,
            default_value: fruitOptions?.[0]?.value,
        },
        growersField,
    ];

    const getFormObjectFromEntity = (entity: Partial<Supplier>, fruitType?: string): Partial<GrowerRelationFormObject> => {
        const selectedFruitType = fruitType || Object.keys(entity.growers || {})[0];

        if (selectedFruitType) {
            const relations = entity.growers?.[selectedFruitType] ?? [];

            return {
                fruit_type: selectedFruitType,
                grower: relations.map((relation) => relation.grower_name),
            };
        }

        return {};
    };

    const onFieldValueChange = (field: string, value: any, formObject: Partial<GrowerRelationFormObject>, entity: Partial<Supplier>) => {
        if (field === "fruit_type") {
            setGrowersField({
                ...growersField,
                options: growerOptions.filter((grower) => (grower as any).fruit_type === value && supplier?.country === (grower as any).country)
            });

            return {
                ...getFormObjectFromEntity(entity, value),
                [field]: value,
            };
        }

        return {
            ...formObject,
            [field]: value,
        } as GrowerRelationFormObject;
    };

    const onSave = (formObject: GrowerRelationFormObject, entity: Partial<Supplier>) => {
        const { fruit_type, grower } = formObject;

        const supplier = {
            ...entity,
            growers: {
                ...entity.growers,
                [fruit_type]: grower.map((grower_name) => ({
                    grower_name,
                    fruit_type,
                }))
            },
        };

        onSaveSupplier(supplier as Supplier);
    };

    const onDelete = (formObject: GrowerRelationFormObject, entity: Partial<Supplier>) => {
        const supplier = {
            ...entity,
            growers: {
                ...entity.growers,
                [formObject.fruit_type]: undefined,
            },
        };

        onSaveSupplier(supplier as Supplier);
    };

    const growerRelationModal: Form<GrowerRelationFormObject, Supplier> = {
        title: "Supplier Grower Relation",
        fields: growerRelationForm,
        modalButtonLabel: "Link Grower(s)",
        onSave,
        onDelete,
        onFieldValueChange,
        getFormObjectFromEntity
    };

    return growerRelationModal;
};
